import type { FC } from 'react'
import { memo } from 'react'

import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/interactives/Tooltip'
import { faEyeSlash, faForward } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'

import type { Cue } from '~shared/rundown/cue'
import { CueType } from '~shared/rundown/cue'

type CueStateIndicatorProps = {
  cueType: CueType
  cueSettings: Cue['settings']
  className?: string
  skipDuringShow: boolean
}

export const CueStateIndicator: FC<CueStateIndicatorProps> = memo(
  function CueStateIndicator({
    cueType,
    cueSettings,
    className,
    skipDuringShow,
  }) {
    const { hideOnCsv, hideOnPdf } = cueSettings ?? {}

    const isHidden = hideOnCsv || hideOnPdf
    const hasIndicator = isHidden || skipDuringShow

    if (!hasIndicator) return null

    const tooltipContent = getCueTooltipText(cueType, {
      hideOnPdf,
      hideOnCsv,
      skipDuringShow,
    })

    return (
      <div
        className={cn('flex items-center justify-center gap-1 z-20', className)}
      >
        <Tooltip>
          <TooltipTrigger>
            <div className="flex items-center gap-1 cursor-help">
              {skipDuringShow && (
                <FontAwesomeIcon
                  icon={faForward}
                  size="xs"
                  className="opacity-50"
                />
              )}
              {isHidden && (
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  size="xs"
                  className="opacity-50"
                />
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>{tooltipContent}</TooltipContent>
        </Tooltip>
      </div>
    )
  },
)

function getHiddenCueTooltipText(type: CueType) {
  if (type === CueType.GROUP) {
    return {
      pdfOnly: 'Group hidden on PDF export (includes all children)',
      csvOnly: 'Group hidden on CSV export (includes all children)',
      both: 'Group hidden on PDF and CSV exports (includes all children)',
    }
  }

  return {
    pdfOnly: 'Hidden on PDF export',
    csvOnly: 'Hidden on CSV export',
    both: 'Hidden on PDF and CSV exports',
  }
}

function getSkipCueTooltipText(type: CueType): string {
  if (type === CueType.GROUP)
    return 'Group skipped during show (includes all children)'
  return 'Skipped during show'
}

function getCueTooltipText(
  type: CueType,
  {
    hideOnPdf,
    hideOnCsv,
    skipDuringShow,
  }: { hideOnPdf?: boolean; hideOnCsv?: boolean; skipDuringShow?: boolean },
): string {
  const parts: string[] = []

  if (skipDuringShow) parts.push(getSkipCueTooltipText(type))

  if (hideOnCsv && hideOnPdf) {
    parts.push(getHiddenCueTooltipText(type).both)
  } else if (hideOnPdf) {
    parts.push(getHiddenCueTooltipText(type).pdfOnly)
  } else if (hideOnCsv) {
    parts.push(getHiddenCueTooltipText(type).csvOnly)
  }

  return parts.join('. ')
}
